import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import {
  VideoLibrary,
  CloudUpload,
  PlayArrow,
  Edit,
  Delete,
  Public,
  School,
  Campaign
} from '@mui/icons-material';
import { apiService } from '@/services/api';
import { DEFAULT_API_BASE_URL } from '@/config/constants';
import { useFilters } from '@/contexts/FilterContext';
import { ProductScopeGate } from '@/components/ProductScopeGate';
import { useMessage } from '@/contexts/MessageContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { logger } from '@/utils/logger';
import {
  CAROUSEL_MEDIA_ACCEPT,
  isAllowedCarouselFile,
  isCarouselImageFilename
} from '@/config/carouselMedia';

export type VideoFolder = 'public' | 'private-tutorials' | 'private-downloads';

export interface VideoItem {
  key: string;
  name: string;
  size: number;
  lastModified: string;
  folder: VideoFolder;
}

type VideosPageProps = {
  allowedFolders?: VideoFolder[];
  title?: string;
  description?: string;
  /** Vídeos privados no prefixo R2 do produto selecionado no filtro global. */
  productScoped?: boolean;
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export const VideosPage: React.FC<VideosPageProps> = ({
  allowedFolders = ['public', 'private-tutorials', 'private-downloads'],
  title = 'Gerenciamento de Vídeos',
  description = 'Biblioteca de mídia no R2 (vídeo .mp4/.webm ou imagem .png/.jpg/.jpeg/.webp/.gif). Máximo 500 MB por arquivo.',
  productScoped = false
}) => {
  const { productId: filterProductId } = useFilters();
  const storageProductId = productScoped ? filterProductId?.trim() : undefined;
  const [tab, setTab] = useState<VideoFolder>(allowedFolders[0] || 'public');
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFolder, setUploadFolder] = useState<VideoFolder>('public');
  const [uploading, setUploading] = useState(false);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [renameKey, setRenameKey] = useState<string | null>(null);
  const [renameName, setRenameName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [actionPreviewKey, setActionPreviewKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const { showSuccess, showError } = useMessage();
  const { setRefreshCallback } = useRefresh();
  const { confirm } = useConfirmation();

  useEffect(() => {
    if (!allowedFolders.includes(tab)) setTab(allowedFolders[0] || 'public');
  }, [allowedFolders, tab]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiService.getVideosStatus();
      setConfigured(res.data?.success && res.data?.configured === true);
    } catch {
      setConfigured(false);
    }
  }, []);

  const loadVideos = useCallback(async () => {
    if (configured === false) return;
    if (productScoped && !storageProductId) {
      setVideos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiService.listVideos(tab, storageProductId);
      if (res.data?.success && Array.isArray(res.data.data)) {
        setVideos(res.data.data);
      } else {
        setVideos([]);
      }
    } catch (err) {
      setLoadError(err);
      logger.error('Error listing videos', err);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [tab, configured, productScoped, storageProductId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  useEffect(() => {
    setRefreshCallback(loadVideos);
    return () => setRefreshCallback(null);
  }, [setRefreshCallback, loadVideos]);

  const handleUpload = async () => {
    if (!uploadFile) {
      showError('Selecione um arquivo válido (.mp4, .webm, .png, .jpg, .jpeg, .webp ou .gif).');
      return;
    }
    if (!isAllowedCarouselFile(uploadFile)) {
      showError(
        'Formato não permitido. Use vídeo (.mp4, .webm) ou imagem (.png, .jpg, .jpeg, .webp, .gif).'
      );
      return;
    }
    setUploading(true);
    try {
      await apiService.uploadVideo(uploadFile, uploadFolder, storageProductId);
      showSuccess('Arquivo enviado com sucesso');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadFolder('public');
      loadVideos();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Erro ao enviar vídeo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (item: VideoItem) => {
    confirm({
      title: 'Excluir vídeo',
      message: `Deseja realmente excluir "${item.name}"?`,
      confirmText: 'Excluir',
      confirmColor: 'error',
      onConfirm: async () => {
        setDeletingKey(item.key);
        try {
          await apiService.deleteVideo(item.key);
          showSuccess('Vídeo excluído');
          loadVideos();
        } finally {
          setDeletingKey(null);
        }
      }
    });
  };

  const handlePreview = async (key: string) => {
    setPreviewKey(key);
    setPreviewUrl(null);
    setPreviewLoading(true);
    setActionPreviewKey(key);
    try {
      const base =
        (process.env.REACT_APP_ADMIN_API_URL || DEFAULT_API_BASE_URL).replace(
          /\/api\/admin\/?$/,
          ''
        ) || DEFAULT_API_BASE_URL;
      const streamUrl = `${base}/videos/stream?key=${encodeURIComponent(key)}`;
      try {
        const response = await fetch(streamUrl, { credentials: 'include' });
        if (!response.ok) throw new Error(String(response.status));
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPreviewUrl(blobUrl);
      } catch {
        const res = await apiService.getVideoPreviewUrl(key);
        const url = res.data?.data?.url;
        if (url) setPreviewUrl(url);
        else {
          showError('Não foi possível carregar o vídeo');
          setPreviewKey(null);
        }
      }
    } catch (err: any) {
      showError(err.response?.data?.message || 'Erro ao carregar preview');
      setPreviewKey(null);
    } finally {
      setPreviewLoading(false);
      setActionPreviewKey(null);
    }
  };

  const handleRenameOpen = (item: VideoItem) => {
    setRenameKey(item.key);
    setRenameName(item.name);
  };

  const handleRename = async () => {
    if (!renameKey || !renameName.trim()) return;
    setRenaming(true);
    try {
      await apiService.renameVideo(renameKey, renameName.trim());
      showSuccess('Vídeo renomeado');
      setRenameKey(null);
      setRenameName('');
      loadVideos();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Erro ao renomear');
    } finally {
      setRenaming(false);
    }
  };

  if (configured === null) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const videosBody =
    configured === false ? (
      <Box>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <VideoLibrary /> Vídeos
        </Typography>
        <Alert severity="warning">
          O armazenamento de vídeos (Cloudflare R2) não está configurado no vover-admin. Configure
          R2_ACCOUNT_ID, R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY e R2_BUCKET_NAME no backend — isso é
          independente do produto selecionado.
        </Alert>
      </Box>
    ) : (
      <Box>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <VideoLibrary /> {title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  {description}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" color="primary" label="Cloudflare R2" />
                  {allowedFolders.includes('public') && (
                    <Chip size="small" variant="outlined" label="Público" />
                  )}
                  {allowedFolders.includes('private-tutorials') && (
                    <Chip size="small" variant="outlined" label="Tutoriais" />
                  )}
                  {allowedFolders.includes('private-downloads') && (
                    <Chip size="small" variant="outlined" label="Divulgação" />
                  )}
                </Stack>
              </Box>
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => {
                  setUploadOpen(true);
                  setUploadFile(null);
                  setUploadFolder(tab);
                }}
              >
                Enviar arquivo
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Paper>
          <Tabs value={tab} onChange={(_, v) => setTab(v as VideoFolder)}>
            {allowedFolders.includes('public') && (
              <Tab value="public" label="Público" icon={<Public />} iconPosition="start" />
            )}
            {allowedFolders.includes('private-tutorials') && (
              <Tab
                value="private-tutorials"
                label="Tutoriais"
                icon={<School />}
                iconPosition="start"
              />
            )}
            {allowedFolders.includes('private-downloads') && (
              <Tab
                value="private-downloads"
                label="Divulgação"
                icon={<Campaign />}
                iconPosition="start"
              />
            )}
          </Tabs>
          <TableContainer>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell align="right">Tamanho</TableCell>
                    <TableCell>Última alteração</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {videos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        Nenhum arquivo nesta pasta. Use &quot;Enviar arquivo&quot; para adicionar.
                      </TableCell>
                    </TableRow>
                  ) : (
                    videos.map(row => (
                      <TableRow key={row.key}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell align="right">{formatSize(row.size)}</TableCell>
                        <TableCell>
                          {row.lastModified
                            ? new Date(row.lastModified).toLocaleString('pt-BR')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            title="Visualizar"
                            onClick={() => handlePreview(row.key)}
                            disabled={!!actionPreviewKey || !!deletingKey}
                          >
                            {actionPreviewKey === row.key ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PlayArrow />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Renomear"
                            onClick={() => handleRenameOpen(row)}
                            disabled={!!actionPreviewKey || !!deletingKey}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            title="Excluir"
                            onClick={() => handleDelete(row)}
                            disabled={
                              !!actionPreviewKey || !!deletingKey || deletingKey === row.key
                            }
                          >
                            {deletingKey === row.key ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <Delete />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Paper>

        {/* Modal Upload */}
        <Dialog
          open={uploadOpen}
          onClose={() => !uploading && setUploadOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Enviar arquivo</DialogTitle>
          <DialogContent>
            <TextField
              select
              fullWidth
              label="Pasta"
              value={uploadFolder}
              onChange={e => setUploadFolder(e.target.value as VideoFolder)}
              margin="normal"
              SelectProps={{ native: true }}
            >
              {allowedFolders.includes('public') && <option value="public">Público</option>}
              {allowedFolders.includes('private-tutorials') && (
                <option value="private-tutorials">Tutoriais (videos/private/tutorials)</option>
              )}
              {allowedFolders.includes('private-downloads') && (
                <option value="private-downloads">Divulgação (videos/private/downloads)</option>
              )}
            </TextField>
            <Button variant="outlined" component="label" fullWidth sx={{ mt: 2 }}>
              {uploadFile ? uploadFile.name : 'Escolher arquivo'}
              <input
                type="file"
                accept={CAROUSEL_MEDIA_ACCEPT}
                hidden
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
              />
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !uploading && setUploadOpen(false)} disabled={uploading}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleUpload} disabled={uploading || !uploadFile}>
              {uploading ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                  Enviando...
                </>
              ) : (
                'Enviar'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Preview */}
        <Dialog
          open={!!previewKey || !!previewUrl}
          onClose={() => {
            if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            setPreviewKey(null);
            setPreviewLoading(false);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Preview {previewKey?.replace(/^public\/|^private\//, '')}</DialogTitle>
          <DialogContent>
            {previewLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }} color="text.secondary">
                  Carregando vídeo...
                </Typography>
              </Box>
            ) : previewUrl ? (
              previewKey && isCarouselImageFilename(previewKey) ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <video
                  controls
                  autoPlay
                  style={{ width: '100%', maxHeight: '70vh' }}
                  src={previewUrl}
                />
              )
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Dialog Renomear */}
        <Dialog
          open={!!renameKey}
          onClose={() => !renaming && setRenameKey(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Renomear vídeo</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Novo nome"
              value={renameName}
              onChange={e => setRenameName(e.target.value)}
              margin="normal"
              placeholder="exemplo.mp4"
              helperText="Extensão: .mp4, .webm, .png, .jpg, .jpeg, .webp ou .gif"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !renaming && setRenameKey(null)} disabled={renaming}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleRename}
              disabled={renaming || !renameName.trim()}
            >
              {renaming ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                  Salvando...
                </>
              ) : (
                'Renomear'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );

  if (productScoped) {
    return (
      <ProductScopeGate
        productId={storageProductId}
        integration="core"
        loading={loading && configured === true}
        loadError={loadError}
        isEmpty={false}
        onRetry={loadVideos}
      >
        {videosBody}
      </ProductScopeGate>
    );
  }

  return videosBody;
};

export default VideosPage;
